import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { BrowserService } from '@core/services/storage.service';
import { AuthService } from '../../../core/services/auth.service';

/**
 * Email verification banner component
 * Displays a sticky banner at the top of the page prompting users to verify their email
 * Only shown to email/password users who haven't verified yet
 */
@Component({
  selector: 'app-email-verification-banner',
  imports: [],
  template: `
    @if (shouldShow()) {
      <div
        class="fixed top-0 right-0 left-0 z-50 bg-linear-to-r from-[#FF5A5F] to-[#FF385C] text-white shadow-lg"
      >
        <div class="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <svg class="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <p class="text-sm font-medium">
                Please verify your email address. Check your inbox for a verification link.
              </p>
            </div>

            <div class="flex items-center gap-3">
              @if (cooldownRemaining() > 0) {
                <button
                  type="button"
                  disabled
                  class="cursor-not-allowed rounded-lg bg-white/20 px-4 py-2 text-sm font-medium text-white/60"
                >
                  Resend in {{ cooldownRemaining() }}s
                </button>
              } @else {
                <button
                  type="button"
                  (click)="resendVerification()"
                  [disabled]="isResending()"
                  class="rounded-lg bg-white px-4 py-2 text-sm font-medium text-[#FF5A5F] transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {{ isResending() ? 'Sending...' : 'Resend Email' }}
                </button>
              }

              <button
                type="button"
                (click)="dismiss()"
                class="rounded-lg p-1 transition-colors hover:bg-white/10"
                aria-label="Dismiss"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          @if (successMessage()) {
            <p class="mt-2 text-sm text-white/90">
              {{ successMessage() }}
            </p>
          }
          @if (errorMessage()) {
            <p class="mt-2 text-sm text-white/90">
              {{ errorMessage() }}
            </p>
          }
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
export class EmailVerificationBannerComponent implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly auth = inject(Auth);
  private readonly browser = inject(BrowserService);
  private readonly currentUser = this.authService.currentUser;

  private readonly isDismissed = signal(false);
  protected readonly isResending = signal(false);
  protected readonly successMessage = signal('');
  protected readonly errorMessage = signal('');
  protected readonly cooldownRemaining = signal(0);
  private cooldownInterval: number | null = null;

  /**
   * Should show the banner?
   * Only shown to:
   * - Email/password users (not OAuth)
   * - Who haven't verified their email
   * - Who haven't dismissed the banner
   */
  readonly shouldShow = computed(() => {
    const user = this.currentUser();
    if (!user || this.isDismissed()) return false;

    // Check if user signed up with email (not OAuth)
    // Use the providers array from AuthUser which contains provider IDs
    const isEmailUser = user.providers.includes('password');

    return isEmailUser && !user.emailVerified;
  });

  constructor() {
    // Check for dismissed state in sessionStorage
    const dismissed = this.browser.getSession('email-verification-banner-dismissed');
    if (dismissed === 'true') {
      this.isDismissed.set(true);
    }

    // Auto-clear messages after 5 seconds using effect
    effect((onCleanup) => {
      if (this.successMessage() || this.errorMessage()) {
        const timeoutId = this.browser.setTimeout(() => {
          this.successMessage.set('');
          this.errorMessage.set('');
        }, 5000);

        onCleanup(() => {
          if (timeoutId !== null) {
            this.browser.clearTimeout(timeoutId);
          }
        });
      }
    });
  }

  /**
   * Resend verification email
   */
  async resendVerification(): Promise<void> {
    if (this.isResending() || this.cooldownRemaining() > 0) return;

    this.isResending.set(true);
    this.successMessage.set('');
    this.errorMessage.set('');

    try {
      await this.authService.resendVerificationEmail();
      this.successMessage.set('✓ Verification email sent! Check your inbox.');
      this.startCooldown();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.errorMessage.set(error.message || 'Failed to send verification email');
    } finally {
      this.isResending.set(false);
    }
  }

  /**
   * Start 60-second cooldown timer
   */
  private startCooldown(): void {
    this.cooldownRemaining.set(60);

    // Clear existing interval if any
    if (this.cooldownInterval !== null) {
      this.browser.clearInterval(this.cooldownInterval);
    }

    this.cooldownInterval = this.browser.setInterval(() => {
      const remaining = this.cooldownRemaining();
      if (remaining <= 1) {
        this.cooldownRemaining.set(0);
        if (this.cooldownInterval !== null) {
          this.browser.clearInterval(this.cooldownInterval);
          this.cooldownInterval = null;
        }
      } else {
        this.cooldownRemaining.set(remaining - 1);
      }
    }, 1000);
  }

  /**
   * Dismiss the banner for this session
   */
  dismiss(): void {
    this.isDismissed.set(true);
    this.browser.setSession('email-verification-banner-dismissed', 'true');
  }

  ngOnDestroy(): void {
    if (this.cooldownInterval !== null) {
      this.browser.clearInterval(this.cooldownInterval);
    }
  }
}
